import React from 'react';
import type { KPISummary } from '../../../utils/accountOwnerUtils';
import { fmtUSD, fmtHours, fmtPct, fmtRate } from '../../../utils/accountOwnerUtils';

interface KpiStripProps {
  kpis: KPISummary;
}

/** One metric inside a grouped KPI card (Currency dashboard pattern) */
const MetricCell: React.FC<{
  label: string;
  value: string;
  sub?: React.ReactNode;
}> = ({ label, value, sub }) => (
  <div className="px-4 py-3 min-w-0 text-sm">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{label}</p>
    <p className="text-sm font-bold text-slate-900 tabular-nums leading-tight">{value}</p>
    {sub && <div className="mt-1 text-xs">{sub}</div>}
  </div>
);

export const KpiStrip: React.FC<KpiStripProps> = ({ kpis }) => {
  const marginPositive = (kpis.totalProfit ?? 0) >= 0;
  const marginPctPositive = (kpis.avgMarginPct ?? 0) >= 0;
  const utilPct =
    kpis.totalHours > 0 ? ((kpis.totalBillable / kpis.totalHours) * 100).toFixed(1) : null;

  return (
    <div className="flex flex-row gap-2 items-stretch min-w-0 w-full">
        {/* Financial — Revenue (+ cost), Profit (+ vs cost · margin %) */}
        <div className="flex-1 min-w-0 bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden flex">
          <div className="w-0.5 flex-none bg-blue-500" aria-hidden />
          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            <MetricCell
              label="Total Revenue"
              value={fmtUSD(kpis.totalRevenue)}
              sub={
                <div className="mt-2">
                  <span
                    className={`text-[11px] font-semibold tabular-nums ${marginPctPositive ? 'text-violet-600' : 'text-orange-600'}`}
                    title="Sum of Cost (raw grade × tech × hrs) — same basis as profit & margin"
                  >
                    Cost {fmtUSD(kpis.totalCost)}
                  </span>
                </div>
              }
            />
            <MetricCell
              label="Profit"
              value={fmtUSD(kpis.totalProfit)}
              sub={
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-semibold tabular-nums">
                  <span className={marginPositive ? 'text-emerald-700' : 'text-red-600'}>
                    {marginPositive ? '↑' : '↓'} vs Cost
                  </span>
                  <span className="text-slate-200">·</span>
                  <span className={marginPctPositive ? 'text-violet-600' : 'text-orange-600'}>
                    Margin {fmtPct(kpis.avgMarginPct)}
                  </span>
                </div>
              }
            />
          </div>
        </div>

        {/* Hours — billable + total */}
        <div className="flex-1 min-w-0 bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden flex">
          <div className="w-0.5 flex-none bg-slate-400" aria-hidden />
          <div className="flex-1 min-w-0 grid grid-cols-2 divide-x divide-slate-100">
            <MetricCell
              label="Billable Hours"
              value={fmtHours(kpis.totalBillable)}
              sub={
                kpis.effectiveBillingRate != null ? (
                  <div className="mt-2">
                    <p className="text-[11px] font-semibold text-blue-600 tabular-nums">
                      Eff. billing rate {fmtRate(kpis.effectiveBillingRate)}
                    </p>
                  </div>
                ) : undefined
              }
            />
            <MetricCell
              label="Total Hours"
              value={fmtHours(kpis.totalHours)}
              sub={
                utilPct != null ? (
                  <p className="mt-2 text-[11px] text-slate-600">
                    Utilization %:{' '}
                    <span className="font-semibold text-slate-700">{utilPct}%</span>
                  </p>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>
  );
};

export default KpiStrip;
